import { Panel } from '@uhg-harmony/uitk-react';
import { widgetTypes } from 'builder/util/constants';
import styled from 'styled-components';
import React, { ReactElement } from 'react';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { Config } from './types';

const { AccordionGroup, Group } = Panel;

const StyledSectionWidget = styled.div``;
const StyledPanel = styled(Panel)<{
  hoverFocusColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: number;
}>`
  & button {
    border-left: 4px solid ${p => p.textColor};
  }
  & button[aria-expanded='true'] {
    border-left: 4px solid ${p => p.textColor} !important;
  }
  & button span {
    color: ${p => p.textColor} !important;
    font-family: ${p => p.fontFamily} !important;
    font-size: ${p => p.fontSize}px !important;
  }
  & button > span > span > svg {
    fill: ${p => p.textColor} !important;
  }
  & button:after {
    box-shadow: 0 4px 0 0 ${p => p.hoverFocusColor} !important;
  }
  & button:focus,
  & button:hover {
    cursor: default;
    box-shadow: 0 0 0 3px ${p => p.hoverFocusColor} !important;
  }
`;

const StyledAccordionGroup = styled(AccordionGroup)`
  border-bottom: none;
`;

const AccordionWidget: React.FC<WidgetRenderProps<Config>> = (
  props: WidgetRenderProps<Config>
): ReactElement => {
  const { currentPage, value, onChange } = props;
  const { id: activeWidgetId, defaultWidget } = props.widgetState;
  const {
    accordionPanels,
    currentTabIndex,
    borderData,
    backgroundData,
    backgroundImage,
    lineColor,
    accordionConnected,
    hoverFocusColor,
    textColor,
    font,
    fontSize,
    visible,
    isCustomWidget,
  } = props.widgetState.config;

  const trackAccordion = useTrackWidget({
    tracking: (props as any).tracking,
    widgetState: props.widgetState,
    currentPage: props.currentPage,
    site: props.site,
  });

  const handlePanelClick = (panelId: number) => {
    if (panelId === null) {
      props.closeAccordionPanel(activeWidgetId, 0);
    } else {
      props.openAccordionPanel(activeWidgetId, getMaxYLocation(panelId));
    }
    onChange(
      {
        ...value,
        currentTabIndex: panelId,
      },
      activeWidgetId,
      defaultWidget
    );
    if (!props.editing) {
      trackAccordion('accordion_widget_toggle', {
        panelId,
        panelName: accordionPanels?.find(p => p.id === panelId)?.name,
        widgetAnalyticsLabel: props.widgetState.config.analyticsLabel,
      });
    }
    const accordionChildren = currentPage.content[activeWidgetId].children;
    accordionChildren.forEach(child => {
      const childWidget = document.getElementById(child);
      const isVisible = currentPage.content[child].tabIndex === panelId;
      if (currentPage.content[child].config.visible !== isVisible) {
        props.changeWidgetVisible(
          props.siteId,
          props.currentPage.pageId,
          activeWidgetId,
          activeWidgetId,
          isVisible,
          panelId
        );
      }
      if (childWidget) {
        // Todo: This z-index setting creates a bug and the image in a card is not shown when the card is a child of an accordion
        if (currentPage.content[child].type !== widgetTypes.Image) {
          if (currentPage.content[child].type === widgetTypes.Card) {
            childWidget.style.zIndex = '1';
          } else {
            childWidget.style.zIndex = '2';
          }
        }
      }
    });
  };

  // Increment 50 per each accordion panel to the total accordionPanels length
  const getIncrementalPanelHeight = (panelLength: number): number => {
    let height = 50;
    for (let i = 0; i < panelLength; i++) {
      height += 50;
    }
    return height;
  };

  const getMaxYLocation = (panelId?: number): number => {
    const { currentPage } = props;
    const panelIndex = panelId !== undefined ? panelId : currentTabIndex;
    let maxY = 120,
      yLocation;
    const parentYLocation =
      currentPage.content[activeWidgetId].config.yLocation;
    Object.values(currentPage.content[activeWidgetId].children).forEach(
      childWidgetId => {
        if (currentPage.content[childWidgetId]?.tabIndex === panelIndex) {
          yLocation =
            currentPage.content[childWidgetId].config.yLocation -
            parentYLocation +
            currentPage.content[childWidgetId].config.height -
            getIncrementalPanelHeight(panelIndex);
        }
        if (yLocation > maxY) maxY = yLocation;
      }
    );
    return maxY;
  };

  let PanelGroupComponent;
  if (accordionConnected) {
    PanelGroupComponent = StyledAccordionGroup;
  } else {
    PanelGroupComponent = Group;
  }

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  return (
    <PanelGroupComponent
      className="PanelGroup"
      data-test-id="grouped-panel"
      onHeaderPress={handlePanelClick}
      open={currentTabIndex}
    >
      {accordionPanels.map((panel, index) => (
        <StyledPanel
          data-test-id={`accordion-panel-${index}`}
          key={panel.id}
          title={panel.name}
          className={`panel-${index}`}
          hoverFocusColor={hoverFocusColor}
          textColor={textColor}
          fontFamily={font}
          fontSize={fontSize}
        >
          <StyledSectionWidget
            data-test-id={`accordion-panel-content-${index}`}
            id={'sectionWidget'}
            className={'sectionWidget'}
            style={{
              backgroundColor:
                backgroundData?.color.startsWith('#') ||
                backgroundData?.color.startsWith('rgb')
                  ? backgroundData?.color
                  : '',
              backgroundImage:
                backgroundData?.style === 'Image'
                  ? `url('${backgroundImage?.url}')`
                  : backgroundData?.style === 'Color' &&
                    !(
                      backgroundData?.color.startsWith('#') ||
                      backgroundData?.color.startsWith('rgb')
                    )
                  ? backgroundData?.color
                  : '',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderWidth: `${borderData?.thickness}px`,
              borderTop: `${
                borderData?.isTopBorderVisible ?? true
                  ? `${borderData?.thickness}px solid`
                  : 'none'
              }`,
              borderBottom: `${
                borderData?.isBottomBorderVisible ?? true
                  ? `${borderData?.thickness}px solid`
                  : 'none'
              }`,
              borderLeft: `${
                borderData?.isLeftBorderVisible ?? true
                  ? `${borderData?.thickness}px solid`
                  : 'none'
              }`,
              borderRight: `${
                borderData?.isRightBorderVisible ?? true
                  ? `${borderData?.thickness}px solid`
                  : 'none'
              }`,
              borderColor: `${lineColor}`,
              borderRadius: `${borderData?.radius}px`,
              backgroundClip: 'content-box',
              width: '100%',
              height: getMaxYLocation(),
              // borderBottom: '1px solid #4B4D4F',
            }}
          />
        </StyledPanel>
      ))}
    </PanelGroupComponent>
  );
};
export default AccordionWidget;
